import {Formik} from 'formik'
import * as Yup from 'yup';
import {FVPHiddenInput, FVPTextInput, FVPTextInputWithSize} from '../../components/formField';
import FootnotesService from '../../services/FootnotesService';
import CFAdminTabs from "../../components/CFAdminTabs";
import {CountryLanguageEnum} from "../../constants/CountryLanguageEnum";
import React, {useState} from "react";
import CFAdminConfirmDialog from "../../components/CFAdminConfirmDialog";

// Creating schema
const schema = Yup.object().shape({
    footNotesId: Yup.number(),
    adminDescId: Yup.number(),
    adminUsSpaDescId: Yup.number(),
    adminCanEngDescId: Yup.number(),
    adminCanFrDescId: Yup.number(),
    footNotesCode: Yup.string()
        .required("Code is a required field")
        .matches(
            /^[A-Za-z0-9_.,()*?!\-\s]+$/,
            "Invalid format. Allowed characters: letters, numbers, space, _ . , ( ) * ? ! -"
        )
        .min(1, "Code must be at least 3 characters")
        .max(10, "Code must be maximum 10 characters"),
    adminDesc: Yup.string()
        .required("Admin Desc is a required field")
        .max(900, "Admin Desc is allowed maximum of 900 characters"),
    adminUsSpaDesc: Yup.string()
        .max(900, "Admin Desc (US - Spanish) is allowed maximum of 900 characters"),
    adminCanEngDesc: Yup.string()
        .max(900, "Admin Desc (Canada - English) is allowed maximum of 900 characters"),
    adminCanFrDesc: Yup.string()
        .max(900, "Admin Desc (Canada - French) is allowed maximum of 900 characters")
});

type CFAdminFootnotesFormProps = {
    footNotesId?: number;
    footNotesCode?: string;
    adminDesc: string;
    adminUsSpaDesc: string;
    adminCanEngDesc: string;
    adminCanFrDesc: string;
    adminDescId?: number;
    adminUsSpaDescId?: number;
    adminCanEngDescId?: number;
    adminCanFrDescId?: number;
    toggleModal: any;
    refreshData: any;
    successMessage: any;
    errorMessage: any;
    isDelete: boolean;
};

function CFAdminFootnotesForm({
                                  footNotesId,
                                  footNotesCode,
                                  adminDesc, adminDescId,
                                  adminUsSpaDesc, adminUsSpaDescId,
                                  adminCanEngDesc, adminCanEngDescId,
                                  adminCanFrDesc, adminCanFrDescId,
                                  toggleModal,
                                  refreshData,
                                  successMessage,
                                  errorMessage,
                                  isDelete
                              }: Readonly<CFAdminFootnotesFormProps>) {

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    const saveFootnotes = (values: any) => {
        const data: any = {
            "footNotesCode": values.footNotesCode,
            "footNotesLanguages": [
                {
                    "countryLanguageId": CountryLanguageEnum.US_ENG,
                    "description": values.adminDesc,
                },
                {
                    "countryLanguageId": CountryLanguageEnum.CAN_ENG,
                    "description": values.adminCanEngDesc,
                },
                {
                    "countryLanguageId": CountryLanguageEnum.CAN_FRA,
                    "description": values.adminCanFrDesc,
                },
                {
                    "countryLanguageId": CountryLanguageEnum.US_SPA,
                    "description": values.adminUsSpaDesc,
                }
            ]
        };

        FootnotesService.create(data)
            .then(() => {
                successMessage(values.footNotesCode + " added successfully.");
                refreshData();
                toggleModal();
            })
            .catch(function (e: { response: { data: { errorMessage: string; }; }; }) {
                errorMessage("Error: " + e.response.data.errorMessage);
            });
    };

    const updateFootnotes = (values: any) => {
        const data = {
            "footNotesCode": values.footNotesCode,
            "footNotesLanguages": [{
                "footNotesLanguageTextId": values.adminDescId,
                "countryLanguageId": CountryLanguageEnum.US_ENG,
                "description": values.adminDesc
            }, {
                "footNotesLanguageTextId": values.adminCanEngDescId,
                "countryLanguageId": CountryLanguageEnum.CAN_ENG,
                "description": values.adminCanEngDesc,
            }, {
                "footNotesLanguageTextId": values.adminCanFrDescId,
                "countryLanguageId": CountryLanguageEnum.CAN_FRA,
                "description": values.adminCanFrDesc,
            }, {
                "footNotesLanguageTextId": values.adminUsSpaDescId,
                "countryLanguageId": CountryLanguageEnum.US_SPA,
                "description": values.adminUsSpaDesc
            }]
        };

        FootnotesService.update(values.footNotesId, data)
            .then(() => {
                successMessage(values.footNotesCode + " updated successfully.");
                refreshData();
                toggleModal();
            })
            .catch(function (e: { response: { data: { errorMessage: string; }; }; }) {
                errorMessage("Error: " + e.response.data.errorMessage);
                console.error("Error message is " + JSON.stringify(e));
            });
    };

    const deleteFootnotes = () => {

        if (footNotesId) {

            FootnotesService.delete(footNotesId)
                .then(() => {
                    successMessage(`footnotes: ${footNotesCode} deleted successfully.`);
                    refreshData();
                    toggleModal();
                })
                .catch((e: any) => {
                    errorMessage("Error while deleting footnotes!");
                    console.error(e);
                });
        } else {
            errorMessage("Error while deleting footnotes!");
        }
    };

    const tabData = [
        {
            label: "USA",
            content: <div><FVPHiddenInput name="adminDescId"/><FVPTextInputWithSize label="Description(English):"
                                                                                    size={900} name="adminDesc" required
                                                                                    readOnly={isDelete}/>
                <FVPHiddenInput name="adminUsSpaDescId"/><FVPTextInputWithSize label="Description(Spanish):" size={900}
                                                                               name="adminUsSpaDesc"
                                                                               readOnly={isDelete}/>
            </div>
        },
        {
            label: "CANADA",
            content: <div><FVPHiddenInput name="adminCanEngDescId"/><FVPTextInputWithSize label="Description(English):"
                                                                                          size={900}
                                                                                          name="adminCanEngDesc"
                                                                                          readOnly={isDelete}/>
                <FVPHiddenInput name="adminCanFrDescId"/><FVPTextInputWithSize label="Description(French):" size={900}
                                                                               name="adminCanFrDesc"
                                                                               readOnly={isDelete}/>
            </div>
        },
    ];

    return (
        <>
            <Formik
                validationSchema={schema}
                initialValues={{
                    footNotesId: footNotesId, footNotesCode: footNotesCode, adminDesc: adminDesc,
                    adminUsSpaDesc: adminUsSpaDesc, adminCanEngDesc: adminCanEngDesc, adminCanFrDesc: adminCanFrDesc,
                    adminDescId: adminDescId, adminUsSpaDescId: adminUsSpaDescId, adminCanEngDescId: adminCanEngDescId,
                    adminCanFrDescId: adminCanFrDescId
                }}
                onSubmit={(values) => {
                    if (isDelete) {
                        setIsDeleteConfirmOpen(true);
                    } else if (footNotesId !== undefined && footNotesId > 0) {
                        updateFootnotes(values);
                    } else {
                        saveFootnotes(values);
                    }
                }}
            >
                {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isValid,
                      dirty
                  }) => (
                    <div className="fvp-form">
                        <div className="wide-data-table">
                            {/* Passing handleSubmit parameter tohtml form onSubmit property */}
                            <form data-testid="SubmitForm" noValidate onSubmit={handleSubmit}>
                                {/* Our input html with passing formik parameters like handleChange, values, handleBlur to input properties */}
                                <div className="modal-body">
                                    <div className="fvp-form__group">
                                        <div className="fvp-form__subgroup">
                                            <FVPHiddenInput name="footNotesId"/>
                                            <FVPTextInput label="Footnotes Code:" name="footNotesCode" required
                                                          readOnly={isDelete}/>
                                        </div>
                                        <CFAdminTabs tabs={tabData}/>
                                    </div>
                                </div>
                                <div className="modal-footer button-frame">
                                    {/*
                  consider a special class for a collection of buttons; their
                  wrapping should probably be done in a special way (submit to the
                  right when laid out in a row, on top when stacked vertically.)
                  */}
                                    <button data-testid="Cancel"
                                            className="fvp-button--outline"
                                            onClick={toggleModal}
                                    >
                                        <i className="fvp-left-chevron"/>{' '}
                                        Cancel
                                    </button>

                                    {isDelete ?
                                        <button
                                            data-testid="Delete"
                                            className="fvp-button-danger--outline"
                                            type="submit"
                                        >
                                            Delete{' '}
                                            <i className="fvp-right-chevron"/>
                                        </button> : <button
                                            data-testid="Save"
                                            className="fvp-button"
                                            type="submit"
                                            disabled={!(dirty && isValid)}

                                        >
                                            {values.footNotesId === undefined || values.footNotesId === 0 ? 'Save' : 'Update'}
                                            <i className="fvp-right-chevron"/>
                                        </button>}
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </Formik>
            <CFAdminConfirmDialog
                isOpen={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                onConfirm={deleteFootnotes}
                title={"Delete Foot Notes : " + footNotesCode}
                text="Are you sure you want to delete this foot notes?"
            />
        </>
    );
}

export default CFAdminFootnotesForm;